import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Modal } from "../components/ui/Modal";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { settingsService } from "../services/settingsService";
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  BarChart3,
  ExternalLink,
  TrendingUp,
  Link as LinkIcon,
} from "lucide-react";

interface SocialLink {
  platform: string;
  url: string;
  isActive: boolean;
  displayOrder: number;
  clicks?: number;
  lastClickedAt?: string;
}

type ModalMode = "add" | "edit" | "analytics" | null;

const PLATFORMS = [
  "facebook",
  "twitter",
  "instagram",
  "linkedin",
  "youtube",
  "tiktok",
  "telegram",
  "whatsapp",
  "snapchat",
  "pinterest",
  "github",
  "discord",
];

const PLATFORM_ICONS: Record<string, string> = {
  facebook: "📘",
  twitter: "🐦",
  instagram: "📷",
  linkedin: "💼",
  youtube: "▶️",
  tiktok: "🎵",
  telegram: "✈️",
  whatsapp: "💬",
  snapchat: "👻",
  pinterest: "📌",
  github: "🐙",
  discord: "🎮",
};

export default function SocialLinksPage() {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedLink, setSelectedLink] = useState<SocialLink | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<SocialLink>>({
    platform: "",
    url: "",
    displayOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    setIsLoading(true);
    try {
      const response: any = await settingsService.getAllSocialLinks();
      setSocialLinks(response.data || []);
    } catch (error) {
      console.error("Failed to fetch social links:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await settingsService.getSocialLinksAnalytics();
      setAnalytics(response);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  };

  const openAddModal = () => {
    setFormData({
      platform: "",
      url: "",
      displayOrder: socialLinks.length,
      isActive: true,
    });
    setModalMode("add");
  };

  const openEditModal = (link: SocialLink) => {
    setSelectedLink(link);
    setFormData({
      platform: link.platform,
      url: link.url,
      displayOrder: link.displayOrder,
      isActive: link.isActive,
    });
    setModalMode("edit");
  };

  const openAnalyticsModal = async () => {
    setModalMode("analytics");
    await fetchAnalytics();
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedLink(null);
  };

  const handleSave = async () => {
    if (!formData.platform || !formData.url) {
      alert("Platform and URL are required");
      return;
    }

    setIsSaving(true);
    try {
      if (modalMode === "add") {
        await settingsService.createSocialLink(formData);
      } else if (modalMode === "edit" && selectedLink) {
        await settingsService.updateSocialLink(selectedLink.platform, formData);
      }
      await fetchSocialLinks();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save social link:", error);
      alert(error.message || "Failed to save social link");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (platform: string) => {
    if (deleteConfirm !== platform) {
      setDeleteConfirm(platform);
      return;
    }

    try {
      await settingsService.deleteSocialLink(platform);
      await fetchSocialLinks();
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete social link:", error);
      alert("Failed to delete social link");
    }
  };

  const handleToggleStatus = async (
    platform: string,
    currentStatus: boolean
  ) => {
    try {
      await settingsService.updateSocialLink(platform, {
        isActive: !currentStatus,
      });
      await fetchSocialLinks();
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  const totalClicks = socialLinks.reduce(
    (sum, link) => sum + (link.clicks || 0),
    0
  );
  const activeLinks = socialLinks.filter((link) => link.isActive).length;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 max-w-7xl mx-auto">
        {/* Header and Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#102040]">Social Links</h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage social media links displayed on your website
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={openAnalyticsModal}
              className="h-11 px-4 border-[#E0E5F2] text-[#102040] hover:bg-gray-50 w-full sm:w-auto"
            >
              <BarChart3 size={18} />
              View Analytics
            </Button>
            <Button
              variant="primary"
              onClick={openAddModal}
              className="h-11 px-6 font-semibold shadow-lg shadow-amber-500/20 w-full sm:w-auto"
            >
              <Plus size={20} />
              Add social link
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-5 border-none shadow-sm bg-white hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Total Links
                </p>
                <p className="text-3xl font-bold text-[#102040]">
                  {socialLinks.length}
                </p>
              </div>
              <div className="p-3.5 bg-blue-50 rounded-2xl">
                <LinkIcon className="h-7 w-7 text-blue-500" />
              </div>
            </div>
          </Card>
          <Card className="p-5 border-none shadow-sm bg-white hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Active Links
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {activeLinks}
                </p>
              </div>
              <div className="p-3.5 bg-green-50 rounded-2xl">
                <Eye className="h-7 w-7 text-green-500" />
              </div>
            </div>
          </Card>
          <Card className="p-5 border-none shadow-sm bg-white hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Total Clicks
                </p>
                <p className="text-3xl font-bold text-purple-600">
                  {totalClicks.toLocaleString()}
                </p>
              </div>
              <div className="p-3.5 bg-purple-50 rounded-2xl">
                <BarChart3 className="h-7 w-7 text-purple-500" />
              </div>
            </div>
          </Card>
        </div>

        {/* Social Links List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#102040]">Link Items</h2>
            <span className="text-sm text-gray-500 font-medium">
              {socialLinks.length} platforms connected
            </span>
          </div>

          {socialLinks.length === 0 ? (
            <Card className="p-12 text-center border-2 border-dashed border-gray-100 bg-gray-50/50">
              <div className="p-4 bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <LinkIcon className="h-10 w-10 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-[#102040] mb-2">
                No social links
              </h3>
              <p className="text-gray-500 max-w-xs mx-auto mb-6">
                Connect your social media platforms to display them on your
                website.
              </p>
              <Button variant="primary" onClick={openAddModal}>
                <Plus size={18} />
                Add your first link
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {socialLinks.map((link) => (
                <Card
                  key={link.platform}
                  className="p-4 border-none shadow-sm hover:shadow-md transition-all duration-200 group relative bg-white overflow-hidden"
                >
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                      link.isActive ? "bg-[#e07828]" : "bg-gray-200"
                    }`}
                  />

                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5 w-full">
                    {/* Platform Icon */}
                    <div className="p-4 bg-gray-50 rounded-2xl group-hover:scale-110 transition-transform duration-300 shrink-0">
                      <span className="text-4xl">
                        {PLATFORM_ICONS[link.platform] || "🔗"}
                      </span>
                    </div>

                    {/* Content Column */}
                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-[#102040] capitalize group-hover:text-[#e07828] transition-colors">
                            {link.platform}
                          </h3>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1.5 mt-0.5"
                          >
                            <span className="truncate max-w-[150px] sm:max-w-xs md:max-w-md">
                              {link.url}
                            </span>
                            <ExternalLink size={12} />
                          </a>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shrink-0 ${
                              link.isActive
                                ? "bg-[#E8F5EE] text-[#1B8754]"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {link.isActive ? "Active" : "Inactive"}
                          </span>
                          <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold bg-[#E6EDFE] text-[#2B3674] uppercase tracking-wider">
                            Order {link.displayOrder}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-6">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#A3AED0]">
                          <BarChart3 size={14} className="text-purple-500" />
                          <span>
                            {link.clicks?.toLocaleString() || 0} clicks
                          </span>
                        </div>
                        {link.lastClickedAt && (
                          <div className="text-xs text-[#A3AED0] flex items-center gap-1.5">
                            <RefreshCw size={12} />
                            <span>
                              Last clicked:{" "}
                              {new Date(
                                link.lastClickedAt
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions Column */}
                    <div className="flex items-center gap-1 mt-3 sm:mt-0 sm:ml-auto w-full sm:w-auto justify-center sm:justify-start">
                      <button
                        onClick={() =>
                          handleToggleStatus(link.platform, link.isActive)
                        }
                        className={`p-2.5 touch-target rounded-xl transition-all duration-200 ${
                          link.isActive
                            ? "text-green-600 bg-green-50 hover:bg-green-100"
                            : "text-gray-400 bg-gray-50 hover:bg-gray-200"
                        }`}
                        title={link.isActive ? "Deactivate" : "Activate"}
                      >
                        {link.isActive ? (
                          <Eye size={20} />
                        ) : (
                          <EyeOff size={20} />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(link)}
                        className="p-2.5 touch-target text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200"
                        title="Edit Link"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => handleDelete(link.platform)}
                        className={`p-2.5 touch-target rounded-xl transition-all duration-200 ${
                          deleteConfirm === link.platform
                            ? "text-red-600 bg-red-100 animate-pulse ring-2 ring-red-500/20"
                            : "text-gray-400 bg-gray-50 hover:bg-red-50 hover:text-red-500"
                        }`}
                        title={
                          deleteConfirm === link.platform
                            ? "Confirm Delete"
                            : "Delete Link"
                        }
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={modalMode === "add" || modalMode === "edit"}
          onClose={closeModal}
          title={modalMode === "add" ? "Add Social Link" : "Edit Social Link"}
          className="max-w-xl"
        >
          <div className="space-y-5 py-2">
            <div>
              <label className="block text-sm font-bold text-[#102040] mb-2 uppercase tracking-wide">
                Platform *
              </label>
              {modalMode === "add" ? (
                <select
                  value={formData.platform}
                  onChange={(e) =>
                    setFormData({ ...formData, platform: e.target.value })
                  }
                  className="w-full h-12 px-4 border-[#E0E5F2] border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-[#e07828] transition-all"
                >
                  <option value="">Select a platform</option>
                  {PLATFORMS.map((platform) => (
                    <option key={platform} value={platform}>
                      {PLATFORM_ICONS[platform]}{" "}
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="h-12 px-4 border border-[#E0E5F2] rounded-xl text-sm bg-gray-50 capitalize flex items-center gap-3 font-bold text-[#102040]">
                  <span className="text-2xl">
                    {PLATFORM_ICONS[formData.platform || ""]}
                  </span>
                  {formData.platform}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#102040] mb-2 uppercase tracking-wide">
                URL *
              </label>
              <Input
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://facebook.com/yourpage"
                icon={<ExternalLink size={18} />}
                className="h-12 rounded-xl"
              />
              <p className="text-[10px] font-semibold text-gray-400 mt-2 uppercase">
                Enter the full URL including https://
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#102040] mb-2 uppercase tracking-wide">
                Display Order
              </label>
              <Input
                type="number"
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    displayOrder: parseInt(e.target.value) || 0,
                  })
                }
                min={0}
                className="h-12 rounded-xl"
              />
              <p className="text-[10px] font-semibold text-gray-400 mt-2 uppercase">
                Lower numbers appear first on the website
              </p>
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="w-5 h-5 rounded-lg border-gray-300 text-[#e07828] focus:ring-amber-500 transition-all cursor-pointer"
                  />
                </div>
                <span className="text-sm font-bold text-[#47548C] group-hover:text-[#102040] transition-colors">
                  Enable this social link on the website
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={closeModal}
              className="px-6 h-11 border-[#E0E5F2] text-[#102040] font-bold"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 h-11 font-bold shadow-lg shadow-amber-500/20"
            >
              {isSaving ? (
                <>
                  <RefreshCw size={18} className="animate-spin mr-2" />{" "}
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />{" "}
                  {modalMode === "add" ? "Create Link" : "Update Link"}
                </>
              )}
            </Button>
          </div>
        </Modal>

        {/* Analytics Modal */}
        <Modal
          isOpen={modalMode === "analytics"}
          onClose={() => setModalMode(null)}
          title="Social Links Analytics"
          className="max-w-4xl"
        >
          <div className="py-2">
            {!analytics ? (
              <div className="flex flex-col items-center justify-center py-16">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-500 mb-4" />
                <p className="text-gray-500 font-medium">
                  Crunching analytics data...
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Summary Card */}
                <Card className="p-8 border-none bg-[#F4F7FE] relative overflow-hidden">
                  <div className="relative z-10 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-[#A3AED0] uppercase tracking-wider mb-2">
                        Total Clicks Across Platforms
                      </p>
                      <p className="text-5xl font-black text-[#102040]">
                        {analytics.totalClicks?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-5 bg-white rounded-3xl shadow-sm shadow-blue-500/10">
                      <TrendingUp className="h-10 w-10 text-blue-500" />
                    </div>
                  </div>
                  {/* Decorative background element */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16" />
                </Card>

                {/* Platform Breakdown */}
                <div>
                  <h3 className="text-lg font-bold text-[#102040] mb-5 px-1">
                    Performance per Platform
                  </h3>
                  <div className="space-y-4">
                    {analytics.data && analytics.data.length > 0 ? (
                      analytics.data.map((item: any) => (
                        <Card
                          key={item.platform}
                          className="p-5 border-none shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-gray-50 rounded-2xl">
                                <span className="text-3xl">
                                  {PLATFORM_ICONS[item.platform] || "🔗"}
                                </span>
                              </div>
                              <div>
                                <p className="text-base font-bold text-[#102040] capitalize">
                                  {item.platform}
                                </p>
                                <p className="text-xs font-medium text-blue-500 truncate max-w-[200px] sm:max-w-md">
                                  {item.url}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-black text-[#102040]">
                                {item.clicks?.toLocaleString() || 0}
                              </p>
                              <p className="text-[10px] font-bold text-[#A3AED0] uppercase tracking-widest">
                                clicks
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              <span>Share of total</span>
                              <span>
                                {analytics.totalClicks > 0
                                  ? Math.round(
                                      (item.clicks / analytics.totalClicks) *
                                        100
                                    )
                                  : 0}
                                %
                              </span>
                            </div>
                            <div className="bg-gray-100 rounded-full h-2.5 overflow-hidden">
                              <div
                                className="bg-linear-to-r from-blue-500 to-purple-500 h-full rounded-full transition-all duration-1000"
                                style={{
                                  width: `${
                                    analytics.totalClicks > 0
                                      ? (item.clicks / analytics.totalClicks) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                            {item.lastClickedAt && (
                              <p className="text-[10px] text-[#A3AED0] font-medium pt-1 italic">
                                Last click recorded on{" "}
                                {new Date(
                                  item.lastClickedAt
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </Card>
                      ))
                    ) : (
                      <div className="py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                        <p className="text-gray-400 font-medium">
                          No click data available yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-6 mt-8 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setModalMode(null)}
              className="px-8 h-11 border-[#E0E5F2] text-[#102040] font-bold"
            >
              Close
            </Button>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
